import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useCompanyStore } from '@/stores/company-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useSnackbarStore } from '@certara/common-react-components';

interface DeleteProps {
  open?: boolean;
  onClose?: () => void;
}

export function Delete({
  open: externalOpen,
  onClose: externalOnClose,
}: DeleteProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external control if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen =
    externalOnClose !== undefined
      ? (newOpen: boolean) => {
          if (!newOpen) externalOnClose();
        }
      : setInternalOpen;
  const { activeCompany } = useCompanyStore();
  const { selectedItems, currentPath, setSelectedItems } = useNavigationStore();
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  const isDisabled = selectedItems.length === 0;

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const objects = selectedItems.map((item) => ({
        IsFolder: item.IsFolder,
        Path: item.Name,
      }));

      const payload = {
        companyId: activeCompany!.Id,
        objects,
        currentLocationPath: currentPath || '', // Ensure it's always a string, never undefined
      };

      return apiClient.postForm<
        { IsSuccess: boolean; Message?: string; Path?: string; ErrorMessage?: string }[]
      >('api/Storage/Delete', payload);
    },
    onSuccess: (results) => {
      if (!results) {
        addSnackbar('Delete operation completed', 'success');
        setSelectedItems([]);
        setOpen(false);
        queryClient.invalidateQueries({ queryKey: ['gridData'] });
        return;
      }

      const failed = results.filter((r) => !r.IsSuccess);

      if (failed.length > 0) {
        addSnackbar(
          `Failed to delete ${failed.length} item(s)`,
          'error',
          failed.map((f) => `${f.Path}: ${f.ErrorMessage}`).join('\n'),
          true
        );
      } else {
        addSnackbar('Items deleted successfully', 'success');
      }

      setSelectedItems([]);
      setOpen(false);
      // Invalidate the specific grid data query for current company and path
      queryClient.invalidateQueries({
        queryKey: ['gridData', activeCompany!.Id, currentPath],
      });
    },
    onError: (error: Error) => {
      addSnackbar('Delete operation failed', 'error', error.message, true);
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  return (
    <>
      {/* Render button only when not externally controlled */}
      {externalOpen === undefined && (
        <Button
          color="inherit"
          startIcon={<DeleteIcon />}
          disabled={isDisabled}
          onClick={() => setOpen(true)}
          sx={{ color: 'white' }}
        >
          Delete
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="delete-dialog"
      >
        <DialogTitle data-testid="delete-dialog-title">Delete</DialogTitle>
        <DialogContent>
          <Typography data-testid="delete-dialog-message">
            You are about to delete{' '}
            {selectedItems.length > 1
              ? `${selectedItems.length} items`
              : selectedItems[0]?.Name}
            .
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="error" data-testid="delete-dialog-warning">
              This removes the file(s) or folder(s) from the file system permanently and
              is not recoverable!
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography variant="body2">Click DELETE to continue.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="delete-dialog-confirm-button"
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
          <Button
            onClick={() => setOpen(false)}
            disabled={deleteMutation.isPending}
            data-testid="delete-dialog-cancel-button"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
